import * as React from 'react';
import { useState } from 'react';

import { Info } from 'lucide-react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SingleSelect, SingleSelectOption } from '@/modules/common';
import { formatDateRange } from '@/utils/date-formatters';

import { getChangesTypeMap } from '../../constants/team-member-changes-table-columns';
import { useDeleteTeamMemberChange } from '../../hooks/use-delete-team-member-change';
import {
  TeamMemberChangeDeletionConflictData,
  TeamMemberChangeExtensionOption,
} from '../../types/team-member-change-deletion';

interface DeleteTeamMemberChangeModalProps {
  conflictData: TeamMemberChangeDeletionConflictData;
  teamMemberId: number;
  onSuccess: () => void;
  open: boolean;
  onClose: () => void;
}

export function DeleteTeamMemberChangeModal({
  conflictData,
  teamMemberId,
  onSuccess,
  open,
  onClose,
}: DeleteTeamMemberChangeModalProps) {
  const { t } = useTranslation(['authenticated.team', 'common']);

  const [selectedOption, setSelectedOption] = useState<TeamMemberChangeExtensionOption | null>(
    null
  );

  const deleteChangeMutation = useDeleteTeamMemberChange();

  const handleDelete = async () => {
    if (!selectedOption) return;

    try {
      await deleteChangeMutation.mutateAsync({
        teamMemberId,
        section: conflictData.changeToDelete.type,
        stateDeltaId: conflictData.changeToDelete.id,
        extendDirection: selectedOption.direction,
      });
      onSuccess();
      onClose();
    } catch {
      // Error handled by mutation
    }
  };

  const typeMapper = getChangesTypeMap();
  const typeData = typeMapper[conflictData.changeToDelete.type];

  const deletedRangeText = formatDateRange(
    conflictData.changeToDelete.startDate,
    conflictData.changeToDelete.endDate
  );

  const form = useForm<{ resultingValue: string }>({
    defaultValues: {
      resultingValue: '',
    },
  });

  const selectOptions: SingleSelectOption[] = React.useMemo(
    () =>
      conflictData.availableOptions.map(option => ({
        value: option.direction,
        label: option.label,
        description: option.dateRange,
      })),
    [conflictData.availableOptions]
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="flex max-h-[85vh] max-w-[75vw] flex-col"
        onPointerDownOutside={e => e.preventDefault()}
        onEscapeKeyDown={e => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {t('authenticated.team:details.tabs.changes.deleteModal.title')}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          {t('authenticated.team:details.tabs.changes.deleteModal.description', {
            dateRange: deletedRangeText,
          })}
        </p>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <FormProvider {...form}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">
                    {t('authenticated.team:details.tabs.changes.table.type')}
                  </TableHead>
                  <TableHead className="w-[300px]">
                    {t('authenticated.team:details.tabs.changes.deleteModal.deletedRange')}
                  </TableHead>
                  <TableHead className="w-[250px]">
                    {t('authenticated.team:details.tabs.changes.deleteModal.resultingValue')}
                  </TableHead>
                  <TableHead className="w-[300px]">
                    {t('authenticated.team:details.tabs.changes.deleteModal.extendPrevious')}
                  </TableHead>
                  <TableHead className="w-[300px]">
                    {t('authenticated.team:details.tabs.changes.deleteModal.shiftNext')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {typeData.icon}
                      <span>{typeData.label}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {formatDateRange(
                        conflictData.changeToDelete.startDate,
                        conflictData.changeToDelete.endDate
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="min-w-[250px] max-w-[250px]">
                      <SingleSelect
                        name="resultingValue"
                        control={form.control}
                        options={selectOptions}
                        placeholder={t(
                          'authenticated.team:details.tabs.changes.deleteModal.selectOption'
                        )}
                        searchable={false}
                        showDescriptionInOptions={true}
                        showDescriptionInSelector={false}
                        isClearSelectionEnabled={false}
                        renderTrigger={(selectedOption, placeholder) =>
                          selectedOption ? (
                            <div className="flex min-w-0 flex-1 flex-col py-2">
                              <div className="truncate text-start">{selectedOption.label}</div>
                              {selectedOption.description && (
                                <div className="truncate text-start text-sm text-muted-foreground">
                                  {selectedOption.description}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">{placeholder}</span>
                          )
                        }
                        onChange={value => {
                          const option = conflictData.availableOptions.find(
                            opt => opt.direction === value
                          );
                          setSelectedOption(option || null);
                        }}
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {conflictData.previousChange
                        ? formatDateRange(
                            conflictData.previousChange.startDate,
                            conflictData.previousChange.endDate
                          )
                        : ''}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {conflictData.nextChange
                        ? formatDateRange(
                            conflictData.nextChange.startDate,
                            conflictData.nextChange.endDate
                          )
                        : ''}
                    </span>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </FormProvider>
        </div>

        <Alert variant="info">
          <Info className="h-4 w-4" />
          <AlertDescription>
            {t('authenticated.team:details.tabs.changes.deleteModal.alertMessage', {
              dateRange: deletedRangeText,
            })}
          </AlertDescription>
        </Alert>

        <DialogFooter className="flex flex-row items-center justify-end gap-2 sm:justify-end">
          <Button variant="outline" onClick={onClose} disabled={deleteChangeMutation.isPending}>
            {t('common:cancel')}
          </Button>
          <ButtonLoadable
            variant="destructive"
            onClick={handleDelete}
            disabled={!selectedOption}
            isLoading={deleteChangeMutation.isPending}
          >
            {deleteChangeMutation.isPending
              ? t('authenticated.team:details.tabs.changes.deleteModal.deleting')
              : t('authenticated.team:details.tabs.changes.deleteModal.delete')}
          </ButtonLoadable>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
